<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" lg="10" offset-lg="1">
        <h2 class="title mb-3">画像一覧</h2>
        <permission-guard situation="picture_management">
          <v-btn color="primary" dark tile @click.stop="showUploadDialog">
            画像アップロード
          </v-btn>
          <v-btn
            class="ml-2"
            color="success"
            dark
            tile
            @click.stop="showMultipleUploadDialog"
          >
            一括アップロード
          </v-btn>
          <v-container fluid class="px-0">
            <v-row>
              <v-col
                v-for="picture in pictures"
                :key="picture.id"
                class="d-flex child-flex"
                cols="3"
              >
                <v-card
                  flat
                  tile
                  class="d-flex"
                  @click.stop="showEditDialog(picture)"
                >
                  <v-img
                    :src="picture.file.middle.url"
                    aspect-ratio="1"
                    class="grey lighten-2"
                    max-width="300"
                    max-height="200"
                  >
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="grey lighten-5"
                        />
                      </v-row>
                    </template>
                  </v-img>
                </v-card>
              </v-col>
            </v-row>
            <infinite-loading
              spinner="spiral"
              class="mt-2"
              @infinite="infiniteHandler"
            >
              <template v-slot:no-more>
                <v-chip label outlined> 全て表示されました。 </v-chip>
              </template>
              <template v-slot:no-results>
                <v-chip label outlined> 表示できるものがありません。 </v-chip>
              </template>
            </infinite-loading>
          </v-container>
        </permission-guard>
      </v-col>
    </v-row>

    <v-dialog v-model="uploadDialog" max-width="400">
      <v-card>
        <v-card-title> 画像アップロード </v-card-title>
        <v-card-text>
          <v-form ref="newFileForm">
            <v-text-field
              v-model="picture.name"
              :counter="40"
              label="画像タイトル"
              required
            />
            <ImageFormAndThumbnail
              ref="imageFormAndThumbnail"
              label-name='画像ファイル'
              :requiredImageFile="true"
              @change-image-file="(value) => picture.file = value"
            ></ImageFormAndThumbnail>
            <v-row>
              <v-col cols="5">
                <v-switch v-model="picture.isMoral" label="健全な画像" />
              </v-col>
              <v-col cols="5">
                <v-switch v-model="picture.isGood" label="良い画像" />
              </v-col>
            </v-row>
            <v-textarea
              v-model="picture.tags"
              class="body-2"
              auto-grow
              label="画像タグ"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="green darken-1" text @click="uploadPicture">
            アップロード
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="multipleUploadDialog" max-width="400">
      <v-card>
        <v-card-title> 画像一括アップロード </v-card-title>
        <v-card-text>
          <v-form ref="multipleFileForm">
            <v-file-input
              v-model="newPictures"
              show-size
              autofocus
              :rules="[sizeValidation]"
              accept="image/*"
              multiple
              label="画像ファイル"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="green darken-1" text @click="uploadMultiplePictures">
            アップロード
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="400">
      <v-card>
        <v-card-title> 画像編集 </v-card-title>
        <v-card-text>
          <v-form ref="newFileForm">
            <v-text-field
              v-model="picture.name"
              :counter="40"
              label="画像タイトル"
              disabled
              required
            />
            <v-row>
              <v-col cols="5">
                <v-switch
                  v-model="picture.isMoral"
                  label="健全な画像"
                  disabled
                />
              </v-col>
              <v-col cols="5">
                <v-switch v-model="picture.isGood" label="良い画像" disabled />
              </v-col>
            </v-row>
            <v-textarea
              v-model="picture.tags"
              class="body-2"
              auto-grow
              label="画像タグ"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="green darken-1" text @click="editPicture(picture)">
            編集
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import PermissionGuard from '@/components/commons/PermissionGuard'
import headUtilsMixin from '@/mixins/head_utils'
import InfiniteLoading from 'vue-infinite-loading'
import ImageFormAndThumbnail from '@/components/commons/ImageFormAndThumbnail'

export default {
  components: { PermissionGuard, InfiniteLoading, ImageFormAndThumbnail },
  mixins: [headUtilsMixin],
  data: () => {
    return {
      pageTitle: '画像一覧',
      page: 1,
      uploadDialog: false,
      multipleUploadDialog: false,
      editDialog: false,
      newPictures: [],
      pictures: [],
      picture: {
        id: null,
        name: '',
        file: null,
        isGood: true,
        isMoral: true,
        tags: ''
      },
      sizeValidation: (value) => {
        if (value.length === 0) {
          return '必ずファイルを入力してください'
        }
        const size = value.reduce(function (a, b) {
          return { size: a.size + b.size }
        })

        if (size.size > 20000000) {
          return '一度にアップロードできるのは20MBまでです'
        } else {
          return true
        }
      }
    }
  },
  methods: {
    infiniteHandler ($state) {
      axios
        .get('/api/pictures', {
          params: {
            page: this.page
          }
        })
        .then(({ data }) => {
          const fetchedPictures = data.data
          if (fetchedPictures.length) {
            this.page++
            for (const i in fetchedPictures) {
              const post = fetchedPictures[i].attributes
              this.pictures.push(post)
            }
            $state.loaded()
          } else {
            $state.complete()
          }
        })
    },
    showUploadDialog () {
      const newPicture = {
        name: '',
        file: null,
        isGood: true,
        isMoral: true
      }
      this.picture = newPicture
      this.uploadDialog = true
    },
    showEditDialog (picture) {
      const editPicture = {
        id: picture.id,
        name: picture.name,
        file: picture.file.middle.url,
        isGood: picture.isGood,
        isMoral: picture.isMoral,
        tags: picture.tags
      }
      this.picture = editPicture
      this.editDialog = true
    },
    showMultipleUploadDialog () {
      this.newPictures = []
      this.multipleUploadDialog = true
    },
    uploadMultiplePictures () {
      if (!this.$refs.multipleFileForm.validate()) {
        return false
      }

      this.$store.dispatch('LoaderStore/displayLoader')
      let formData = new FormData()
      this.newPictures.forEach((picture) => {
        formData.append('picture_files[]', picture)
      })
      axios
        .post('/api/multiple_pictures', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(
          ({ data }) => {
            const fetchedPictures = data.data
            let uploadedPictures = []
            for (const i in fetchedPictures) {
              uploadedPictures.push(fetchedPictures[i].attributes)
            }
            this.pictures.unshift(...uploadedPictures)
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: 200,
              message: '画像を一括アップロードしました'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: error.response.status,
              message: error.response.data.errors.toString()
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
      this.multipleUploadDialog = false
    },
    uploadPicture () {
      if (!this.$refs.newFileForm.validate()) {
        return false
      }
      this.$store.dispatch('LoaderStore/displayLoader')
      const payload = this.picture
      let formData = new FormData()
      const fileName = payload.name ? payload.name : payload.file.name
      formData.append('picture[name]', fileName)
      formData.append('picture[file]', payload.file)
      formData.append('picture[is_good]', payload.isGood)
      formData.append('picture[is_moral]', payload.isMoral)
      formData.append('picture[tags]', payload.tags)
      axios
        .post('/api/pictures', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(
          ({ data }) => {
            const picture = data.data.attributes
            this.pictures.unshift(picture)
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: 200,
              message: '画像をアップロードしました'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
            this.uploadDialog = false
            this.$refs.imageFormAndThumbnail.imageFile = null
            this.$refs.imageFormAndThumbnail.imagePreviewURL = null
          },
          (error) => {
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: error.response.status,
              message: error.response.data.errors.toString()
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
    },
    editPicture (picture) {
      this.$store.dispatch('LoaderStore/displayLoader')
      const payload = this.picture
      let formData = new FormData()
      formData.append('picture[tags]', payload.tags)
      axios
        .patch(`/api/pictures/${picture.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(
          ({ data }) => {
            const picture = data.data.attributes
            const index = this.pictures.findIndex((v) => v.id === picture.id)
            this.pictures.splice(index, 1, data.data.attributes)
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: 200,
              message: '画像を編集しました'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
            this.editDialog = false
          },
          (error) => {
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: error.response.status,
              message: error.response.data.errors.toString()
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
    }
  }
}
</script>

<style scoped></style>
